import type { Span } from './types.ts';

/**
 * Spans in, DOM out, and back again.
 *
 * Nothing here ever assigns innerHTML from stored text: every node is built
 * with createElement and createTextNode, so a page cannot contain markup that
 * the browser would run. Links are checked against a scheme allowlist for the
 * same reason.
 */

const MARKS = ['b', 'i', 's', 'u', 'c'] as const;

export function plainOf(spans: Span[]): string {
  return spans.map((s) => s.t).join('');
}

export function isEmpty(spans: Span[]): boolean {
  return plainOf(spans).length === 0;
}

export function normalize(spans: Span[]): Span[] {
  const out: Span[] = [];
  for (const span of spans) {
    if (!span.t) continue;
    const prev = out.at(-1);
    if (prev && sameMarks(prev, span)) prev.t += span.t;
    else out.push({ ...span });
  }
  return out;
}

function sameMarks(a: Span, b: Span): boolean {
  if ((a.l ?? '') !== (b.l ?? '') || (a.k ?? '') !== (b.k ?? '')) return false;
  return MARKS.every((m) => !!a[m] === !!b[m]);
}

export function safeHref(href: string): string | null {
  const trimmed = href.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  // A bare domain is what people actually type.
  if (/^[\w-]+(\.[\w-]+)+(\/|$)/.test(trimmed)) return `https://${trimmed}`;
  return null;
}

// ---- spans to DOM ---------------------------------------------------------

export function spansToFragment(spans: Span[]): DocumentFragment {
  const frag = document.createDocumentFragment();
  for (const span of spans) {
    let node: Node = document.createTextNode(span.t);
    if (span.c) node = wrap('code', node);
    if (span.b) node = wrap('strong', node);
    if (span.i) node = wrap('em', node);
    if (span.u) node = wrap('u', node);
    if (span.s) node = wrap('s', node);
    if (span.k) {
      const el = document.createElement('span');
      el.className = `tint tint-${span.k.replace(/[^a-z_]/gi, '')}`;
      el.appendChild(node);
      node = el;
    }
    if (span.l) {
      const href = safeHref(span.l);
      if (href) {
        const el = document.createElement('a');
        el.setAttribute('href', href);
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer nofollow');
        el.appendChild(node);
        node = el;
      }
    }
    frag.appendChild(node);
  }
  return frag;
}

function wrap(tag: string, child: Node): HTMLElement {
  const el = document.createElement(tag);
  el.appendChild(child);
  return el;
}

/** Replaces an element's contents without ever touching innerHTML. */
export function renderInto(el: HTMLElement, spans: Span[]): void {
  el.replaceChildren(spansToFragment(spans));
}

// ---- DOM back to spans ----------------------------------------------------

export function spansFrom(root: HTMLElement): Span[] {
  const out: Span[] = [];
  walk(root, {}, out);
  return normalize(out);
}

function walk(node: Node, marks: Partial<Span>, out: Span[]): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out.push({ ...marks, t: child.nodeValue ?? '' });
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const el = child as HTMLElement;
    const tag = el.tagName;

    if (tag === 'BR') { out.push({ ...marks, t: '\n' }); continue; }

    const next: Partial<Span> = { ...marks };
    if (tag === 'B' || tag === 'STRONG') next.b = 1;
    else if (tag === 'I' || tag === 'EM') next.i = 1;
    else if (tag === 'S' || tag === 'STRIKE' || tag === 'DEL') next.s = 1;
    else if (tag === 'U') next.u = 1;
    else if (tag === 'CODE' || tag === 'TT') next.c = 1;
    else if (tag === 'A') {
      const href = safeHref(el.getAttribute('href') ?? '');
      if (href) next.l = href;
    } else if (el.classList.contains('tint')) {
      const tint = [...el.classList].find((c) => c.startsWith('tint-'));
      if (tint) next.k = tint.slice(5);
    }

    // A pasted paragraph or list item is a line of its own.
    const breaks = /^(DIV|P|LI|TR|H[1-6]|BLOCKQUOTE)$/.test(tag);
    if (breaks && out.length && !out.at(-1)!.t.endsWith('\n')) out.push({ t: '\n' });
    walk(el, next, out);
    if (breaks) out.push({ t: '\n' });
  }
}

// ---- caret ----------------------------------------------------------------

/** Plain-text offset of the caret inside `root`, or null if it is elsewhere. */
export function caretOffset(root: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || !sel.focusNode || !root.contains(sel.focusNode)) return null;
  const range = document.createRange();
  range.selectNodeContents(root);
  range.setEnd(sel.focusNode, sel.focusOffset);
  return measure(range.cloneContents());
}

function measure(frag: DocumentFragment | HTMLElement): number {
  let n = 0;
  const walker = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) n += (node.nodeValue ?? '').length;
    else if ((node as HTMLElement).tagName === 'BR') n += 1;
    node = walker.nextNode();
  }
  return n;
}

export function placeCaret(root: HTMLElement, offset: number | 'start' | 'end'): void {
  const total = textLength(root);
  const target = offset === 'start' ? 0 : offset === 'end' ? total : Math.max(0, Math.min(offset, total));

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let seen = 0;
  let node = walker.nextNode() as Text | null;
  const range = document.createRange();

  while (node) {
    const len = node.nodeValue?.length ?? 0;
    if (seen + len >= target) {
      range.setStart(node, target - seen);
      range.collapse(true);
      applyRange(range);
      return;
    }
    seen += len;
    node = walker.nextNode() as Text | null;
  }

  // No text at all, or the offset is past the end.
  range.selectNodeContents(root);
  range.collapse(false);
  applyRange(range);
}

function applyRange(range: Range): void {
  const sel = window.getSelection();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
}

export function textLength(root: HTMLElement): number {
  return measure(root);
}

/** True when the caret sits on the first (or last) visual line of the block. */
export function onEdgeLine(root: HTMLElement, edge: 'first' | 'last'): boolean {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return true;
  const caret = sel.getRangeAt(0).getBoundingClientRect();
  const box = root.getBoundingClientRect();
  // An empty block gives a zero rect; treat it as both edges.
  if (!caret.height) return true;
  return edge === 'first' ? caret.top - box.top < caret.height * 0.75
                          : box.bottom - caret.bottom < caret.height * 0.75;
}

// ---- editing spans --------------------------------------------------------

/** Splits spans at a plain-text offset, keeping marks on both halves. */
export function splitSpans(spans: Span[], offset: number): [Span[], Span[]] {
  const before: Span[] = [];
  const after: Span[] = [];
  let seen = 0;
  for (const span of spans) {
    const len = span.t.length;
    if (seen >= offset) after.push({ ...span });
    else if (seen + len <= offset) before.push({ ...span });
    else {
      before.push({ ...span, t: span.t.slice(0, offset - seen) });
      after.push({ ...span, t: span.t.slice(offset - seen) });
    }
    seen += len;
  }
  return [normalize(before), normalize(after)];
}

export function sliceSpans(spans: Span[], start: number, end: number): Span[] {
  const [, rest] = splitSpans(spans, start);
  const [mid] = splitSpans(rest, end - start);
  return mid;
}

/** Turns a mark on across a range, or off if every character already has it. */
export function toggleMark(
  spans: Span[],
  start: number,
  end: number,
  mark: 'b' | 'i' | 's' | 'u' | 'c',
): Span[] {
  if (start === end) return spans;
  const [head, rest] = splitSpans(spans, start);
  const [middle, tail] = splitSpans(rest, end - start);
  const on = middle.every((s) => s[mark]);
  const flipped = middle.map((s) => {
    const next = { ...s };
    if (on) delete next[mark];
    else next[mark] = 1;
    return next;
  });
  return normalize([...head, ...flipped, ...tail]);
}

export function applyLink(spans: Span[], start: number, end: number, href: string | null): Span[] {
  if (start === end) return spans;
  const [head, rest] = splitSpans(spans, start);
  const [middle, tail] = splitSpans(rest, end - start);
  const linked = middle.map((s) => {
    const next = { ...s };
    if (href) next.l = href;
    else delete next.l;
    return next;
  });
  return normalize([...head, ...linked, ...tail]);
}

export function insertText(spans: Span[], offset: number, text: string): Span[] {
  const [head, tail] = splitSpans(spans, offset);
  const carry = head.at(-1);
  const marks: Partial<Span> = carry ? { ...carry, t: undefined as unknown as string } : {};
  delete (marks as Record<string, unknown>).t;
  return normalize([...head, { ...marks, t: text }, ...tail]);
}
